/**
 * CareerIllustration.tsx
 *
 * Programmatic SVG illustration system for career cards.
 *
 * Recipe per card:
 *   Layer 1 — Gradient background (category colours)
 *   Layer 2 — 2 large floating circles (hash-seeded positions)
 *   Layer 3 — Centered icon (white paths, ~60×60 area)
 *   Layer 4 — 5 small accent dots (hash-seeded scatter)
 *
 * Adding a new field node: add its nodeId prefix → VISUAL_MAP entry.
 * New icon shapes: add an ICON_TYPE entry + renderIcon branch.
 */

import React from "react"

// ─── Hash utilities ───────────────────────────────────────────────────────────

function nodeHash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return h >>> 0
}

function seededRand(seed: number) {
  let s = seed | 1
  return (): number => {
    s ^= s << 13; s ^= s >> 17; s ^= s << 5
    return ((s >>> 0) & 0xffff) / 0xffff
  }
}

// ─── Visual config ────────────────────────────────────────────────────────────

type IconType =
  | "code" | "ai" | "data" | "shield" | "cloud" | "chip" | "brush" | "vr"
  | "bridge" | "gear" | "bolt" | "flask" | "atom" | "dna" | "sigma" | "chart_bar"
  | "stethoscope" | "cross" | "mortar" | "leaf" | "fish" | "wheat" | "seedling"
  | "coin" | "bank" | "trend" | "palette" | "pen" | "mask" | "scales"
  | "mic" | "brain" | "globe" | "book" | "camera" | "law_hammer"

interface VisualConfig {
  g1: string  // gradient start (hex)
  g2: string  // gradient end   (hex)
  iconType: IconType
  floatOpacity?: number  // floating circle opacity (default 0.15)
}

// nodeId exact-match first, then prefix match
const VISUAL_MAP: Record<string, VisualConfig> = {
  // ── Technology ───────────────────────────────────────────────────────────
  field_tech_software_engineering:     { g1: "#3B82F6", g2: "#1D4ED8", iconType: "code" },
  field_tech_ai_ml:                    { g1: "#8B5CF6", g2: "#5B21B6", iconType: "ai" },
  field_tech_data_science:             { g1: "#06B6D4", g2: "#0E7490", iconType: "data" },
  field_tech_cybersecurity:            { g1: "#EF4444", g2: "#B91C1C", iconType: "shield" },
  field_tech_cloud_devops:             { g1: "#0EA5E9", g2: "#0369A1", iconType: "cloud" },
  field_tech_hardware_electronics:     { g1: "#64748B", g2: "#1E293B", iconType: "chip" },
  field_tech_product_design:           { g1: "#EC4899", g2: "#BE185D", iconType: "brush" },
  field_tech_immersive:                { g1: "#F59E0B", g2: "#B45309", iconType: "vr" },
  field_tech_policy_ethics:            { g1: "#6366F1", g2: "#4338CA", iconType: "scales" },
  field_tech_business_strategy:        { g1: "#10B981", g2: "#047857", iconType: "trend" },
  field_tech_education:                { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Engineering ──────────────────────────────────────────────────────────
  field_eng_civil_infrastructure:      { g1: "#F97316", g2: "#C2410C", iconType: "bridge" },
  field_eng_mechanical_aerospace:      { g1: "#94A3B8", g2: "#334155", iconType: "gear" },
  field_eng_electrical_computer:       { g1: "#FBBF24", g2: "#D97706", iconType: "bolt" },
  field_eng_specialized:               { g1: "#A78BFA", g2: "#7C3AED", iconType: "flask" },
  field_eng_education:                 { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Science ──────────────────────────────────────────────────────────────
  field_sci_life_biological:           { g1: "#10B981", g2: "#059669", iconType: "dna" },
  field_sci_physical_chemical:         { g1: "#7C3AED", g2: "#5B21B6", iconType: "flask" },
  field_sci_medical_clinical:          { g1: "#EF4444", g2: "#B91C1C", iconType: "stethoscope" },
  field_sci_agricultural_veterinary:   { g1: "#84CC16", g2: "#4D7C0F", iconType: "seedling" },
  field_sci_earth_environmental:       { g1: "#22C55E", g2: "#15803D", iconType: "globe" },
  field_sci_emerging_interdisciplinary:{ g1: "#8B5CF6", g2: "#6D28D9", iconType: "atom" },
  field_sci_education:                 { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Mathematics ──────────────────────────────────────────────────────────
  field_math_pure:                     { g1: "#6366F1", g2: "#4338CA", iconType: "sigma" },
  field_math_applied:                  { g1: "#3B82F6", g2: "#1D4ED8", iconType: "sigma" },
  field_math_statistics:               { g1: "#06B6D4", g2: "#0284C7", iconType: "chart_bar" },
  field_math_computational:            { g1: "#8B5CF6", g2: "#6D28D9", iconType: "code" },
  field_math_actuarial:                { g1: "#10B981", g2: "#047857", iconType: "chart_bar" },
  field_math_interdisciplinary:        { g1: "#F59E0B", g2: "#B45309", iconType: "sigma" },
  field_math_education:                { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Health ───────────────────────────────────────────────────────────────
  field_health_medicine:               { g1: "#EF4444", g2: "#B91C1C", iconType: "stethoscope" },
  field_health_nursing:                { g1: "#EC4899", g2: "#DB2777", iconType: "cross" },
  field_health_pharmacy:               { g1: "#10B981", g2: "#059669", iconType: "mortar" },
  field_health_public_health:          { g1: "#06B6D4", g2: "#0E7490", iconType: "globe" },
  field_health_veterinary:             { g1: "#84CC16", g2: "#4D7C0F", iconType: "seedling" },

  // ── Agriculture ──────────────────────────────────────────────────────────
  field_agric_agronomy:                { g1: "#84CC16", g2: "#4D7C0F", iconType: "wheat" },
  field_agric_animal_science:          { g1: "#F97316", g2: "#C2410C", iconType: "seedling" },
  field_agric_environment:             { g1: "#22C55E", g2: "#15803D", iconType: "leaf" },
  field_agric_food_science:            { g1: "#F59E0B", g2: "#D97706", iconType: "flask" },
  field_agric_fisheries:               { g1: "#0EA5E9", g2: "#0284C7", iconType: "fish" },

  // ── Business & Finance ───────────────────────────────────────────────────
  field_biz_accounting_auditing:       { g1: "#F59E0B", g2: "#B45309", iconType: "coin" },
  field_biz_banking_financial:         { g1: "#64748B", g2: "#1E293B", iconType: "bank" },
  field_biz_investment_capital:        { g1: "#10B981", g2: "#047857", iconType: "trend" },
  field_biz_actuarial_risk:            { g1: "#6366F1", g2: "#4338CA", iconType: "chart_bar" },
  field_biz_corporate_management:      { g1: "#8B5CF6", g2: "#6D28D9", iconType: "globe" },
  field_biz_macroeconomics_policy:     { g1: "#3B82F6", g2: "#1D4ED8", iconType: "trend" },
  field_biz_microeconomics_industrial: { g1: "#F97316", g2: "#C2410C", iconType: "chart_bar" },
  field_biz_global_trade:              { g1: "#0EA5E9", g2: "#0369A1", iconType: "globe" },
  field_biz_quantitative:              { g1: "#06B6D4", g2: "#0E7490", iconType: "sigma" },
  field_biz_research_consulting:       { g1: "#A78BFA", g2: "#7C3AED", iconType: "brain" },
  field_biz_energy_urban:              { g1: "#22C55E", g2: "#15803D", iconType: "bolt" },
  field_biz_specialized_emerging:      { g1: "#EC4899", g2: "#BE185D", iconType: "trend" },
  field_biz_education:                 { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Humanities / Arts ────────────────────────────────────────────────────
  field_hum_fine_arts:                 { g1: "#F43F5E", g2: "#E11D48", iconType: "palette" },
  field_hum_literature_writing:        { g1: "#8B5CF6", g2: "#6D28D9", iconType: "pen" },
  field_hum_theater_performing:        { g1: "#7C3AED", g2: "#5B21B6", iconType: "mask" },
  field_hum_dance_movement:            { g1: "#F97316", g2: "#EA580C", iconType: "palette" },
  field_hum_design_applied:            { g1: "#EC4899", g2: "#DB2777", iconType: "brush" },
  field_hum_crafts_heritage:           { g1: "#F59E0B", g2: "#B45309", iconType: "palette" },
  field_hum_arts_management:           { g1: "#A78BFA", g2: "#7C3AED", iconType: "coin" },
  field_hum_arts_education:            { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },
  field_hum_language_linguistics:      { g1: "#6366F1", g2: "#4338CA", iconType: "pen" },
  field_hum_law_justice:               { g1: "#475569", g2: "#1E293B", iconType: "scales" },
  field_hum_media_journalism:          { g1: "#0EA5E9", g2: "#0369A1", iconType: "mic" },
  field_hum_political_governance:      { g1: "#6366F1", g2: "#3730A3", iconType: "globe" },
  field_hum_social_work_psychology:    { g1: "#8B5CF6", g2: "#7C3AED", iconType: "brain" },
  field_hum_sociology_anthropology:    { g1: "#F59E0B", g2: "#D97706", iconType: "globe" },
  field_hum_specialized_humanitarian:  { g1: "#10B981", g2: "#047857", iconType: "cross" },
  field_hum_social_education:          { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },

  // ── Education stubs ──────────────────────────────────────────────────────
  "field_edu_training_broad_stem_tech":         { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },
  "field_edu_training_broad_business_finance":  { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },
  "field_edu_training_broad_humanities_social": { g1: "#14B8A6", g2: "#0F766E", iconType: "book" },
}

// Fallback by broadCategory
const BROAD_FALLBACK: Record<string, VisualConfig> = {
  STEM:       { g1: "#3B82F6", g2: "#1D4ED8", iconType: "atom" },
  Business:   { g1: "#F59E0B", g2: "#B45309", iconType: "trend" },
  Humanities: { g1: "#8B5CF6", g2: "#6D28D9", iconType: "book" },
}

function getConfig(nodeId: string, broadCategory?: string): VisualConfig {
  return (
    VISUAL_MAP[nodeId] ??
    BROAD_FALLBACK[broadCategory ?? ""] ??
    { g1: "#64748B", g2: "#334155", iconType: "globe" as IconType }
  )
}

// ─── Icon renderer ────────────────────────────────────────────────────────────
// All icons designed on a 60×60 canvas, centered at (30,30)
// They are translated into the SVG's center (160, 90)

function renderIcon(type: IconType): React.ReactNode {
  // transform: center icon area at 160,90 (320×180 viewBox)
  const g = (children: React.ReactNode) => (
    <g transform="translate(130, 60)" opacity="0.95">{children}</g>
  )

  const W = { strokeWidth: "4.5", strokeLinecap: "round" as const, strokeLinejoin: "round" as const, stroke: "white", fill: "none" }
  const Wt = { strokeWidth: "3.5", strokeLinecap: "round" as const, strokeLinejoin: "round" as const, stroke: "white", fill: "none" }

  switch (type) {
    case "code": return g(<>
      <polyline points="18,10 4,30 18,50" {...W} />
      <polyline points="42,10 56,30 42,50" {...W} />
      <line x1="36" y1="8" x2="24" y2="52" {...Wt} opacity={0.7} />
    </>)

    case "ai": return g(<>
      {/* Neural net: input(left) → hidden → output(right) */}
      <circle cx="8"  cy="20" r="5" fill="white" opacity="0.9"/>
      <circle cx="8"  cy="40" r="5" fill="white" opacity="0.9"/>
      <circle cx="30" cy="12" r="5" fill="white" opacity="0.9"/>
      <circle cx="30" cy="30" r="5" fill="white" opacity="0.9"/>
      <circle cx="30" cy="48" r="5" fill="white" opacity="0.9"/>
      <circle cx="52" cy="30" r="5" fill="white" opacity="0.9"/>
      {[12,22].map((y,i)=> [12,30,48].map((ty,j)=>(
        <line key={`${i}${j}`} x1="13" y1={y} x2="25" y2={ty} stroke="white" strokeWidth="1.8" opacity="0.5"/>
      )))}
      {[12,30,48].map((y,i)=>(
        <line key={i} x1="35" y1={y} x2="47" y2="30" stroke="white" strokeWidth="1.8" opacity="0.5"/>
      ))}
    </>)

    case "data": return g(<>
      {/* Bar chart: 4 bars ascending */}
      <rect x="4"  y="36" width="9" height="18" rx="2" fill="white" opacity="0.5"/>
      <rect x="17" y="26" width="9" height="28" rx="2" fill="white" opacity="0.7"/>
      <rect x="30" y="16" width="9" height="38" rx="2" fill="white" opacity="0.85"/>
      <rect x="43" y="8"  width="9" height="46" rx="2" fill="white" opacity="1"/>
      <line x1="2" y1="55" x2="58" y2="55" stroke="white" strokeWidth="2.5" opacity="0.6"/>
    </>)

    case "shield": return g(<>
      <path d="M30 4 L54 14 L54 30 C54 44 42 53 30 57 C18 53 6 44 6 30 L6 14 Z" {...W}/>
      <polyline points="20,30 27,37 40,22" {...W} strokeWidth="4"/>
    </>)

    case "cloud": return g(<>
      <path d="M14,42 Q8,42 8,34 Q8,26 16,26 Q16,16 26,16 Q34,16 36,22 Q44,20 46,28 Q52,28 52,36 Q52,42 46,42 Z" {...W}/>
      <polyline points="24,52 24,42" {...Wt}/>
      <polyline points="30,56 30,42" {...Wt}/>
      <polyline points="36,52 36,42" {...Wt}/>
    </>)

    case "chip": return g(<>
      <rect x="16" y="16" width="28" height="28" rx="3" {...W}/>
      <rect x="22" y="22" width="16" height="16" rx="2" fill="white" opacity="0.3"/>
      {/* Pins */}
      {[20,28,36].map(x=><line key={x} x1={x} y1="10" x2={x} y2="16" {...Wt}/>)}
      {[20,28,36].map(x=><line key={x} x1={x} y1="44" x2={x} y2="50" {...Wt}/>)}
      {[20,28,36].map(y=><line key={y} x1="10" y1={y} x2="16" y2={y} {...Wt}/>)}
      {[20,28,36].map(y=><line key={y} x1="44" y1={y} x2="50" y2={y} {...Wt}/>)}
    </>)

    case "brush": return g(<>
      <path d="M38,4 L52,18 L22,48 L6,50 L8,34 Z" {...W}/>
      <line x1="28" y1="16" x2="44" y2="32" {...Wt} opacity="0.6"/>
      <path d="M8,50 Q12,56 18,54 Q20,46 14,44 Q10,44 8,50 Z" {...W} strokeWidth="3"/>
    </>)

    case "vr": return g(<>
      {/* VR headset outline */}
      <rect x="4" y="18" width="52" height="28" rx="10" {...W}/>
      <circle cx="21" cy="32" r="7" {...Wt} opacity="0.7"/>
      <circle cx="39" cy="32" r="7" {...Wt} opacity="0.7"/>
      <line x1="28" y1="32" x2="32" y2="32" stroke="white" strokeWidth="2" opacity="0.6"/>
    </>)

    case "bridge": return g(<>
      {/* Suspension bridge */}
      <line x1="2"  y1="46" x2="58" y2="46" stroke="white" strokeWidth="4"/>
      <line x1="14" y1="46" x2="14" y2="18" {...Wt}/>
      <line x1="46" y1="46" x2="46" y2="18" {...Wt}/>
      <path d="M6,18 Q30,8 54,18" {...Wt} opacity="0.8"/>
      {[[14,18],[30,12],[46,18]].map(([x,y],i)=>(
        <line key={i} x1={x} y1={y} x2={30} y2={46} stroke="white" strokeWidth="1.5" opacity="0.5"/>
      ))}
    </>)

    case "gear": return g(<>
      <circle cx="30" cy="30" r="10" {...Wt}/>
      <circle cx="30" cy="30" r="20" {...W}/>
      {/* 8 gear teeth */}
      {Array.from({length:8},(_,i)=>{
        const a = (i*45*Math.PI)/180
        const x1 = 30 + Math.cos(a)*20, y1 = 30 + Math.sin(a)*20
        const x2 = 30 + Math.cos(a)*28, y2 = 30 + Math.sin(a)*28
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="5" strokeLinecap="round"/>
      })}
    </>)

    case "bolt": return g(<>
      <polygon points="36,4 16,32 28,32 24,56 44,28 32,28" fill="white" opacity="0.95"/>
    </>)

    case "flask": return g(<>
      <path d="M22,6 L22,26 L8,50 Q8,56 16,56 L44,56 Q52,56 52,50 L38,26 L38,6" {...W}/>
      <line x1="18" y1="6" x2="42" y2="6" stroke="white" strokeWidth="4"/>
      <circle cx="36" cy="44" r="4"  fill="white" opacity="0.6"/>
      <circle cx="24" cy="50" r="3"  fill="white" opacity="0.5"/>
      <circle cx="42" cy="50" r="2.5" fill="white" opacity="0.45"/>
    </>)

    case "atom": return g(<>
      <circle cx="30" cy="30" r="5" fill="white" opacity="0.95"/>
      <ellipse cx="30" cy="30" rx="26" ry="10" {...Wt} transform="rotate(0,30,30)"  opacity="0.9"/>
      <ellipse cx="30" cy="30" rx="26" ry="10" {...Wt} transform="rotate(60,30,30)" opacity="0.9"/>
      <ellipse cx="30" cy="30" rx="26" ry="10" {...Wt} transform="rotate(120,30,30)" opacity="0.9"/>
    </>)

    case "dna": return g(<>
      {/* Double helix — 5 rungs */}
      <path d="M20,4 C20,12 40,16 40,24 C40,32 20,36 20,44 C20,52 40,56 40,56" {...Wt} opacity="0.9"/>
      <path d="M40,4 C40,12 20,16 20,24 C20,32 40,36 40,44 C40,52 20,56 20,56" {...Wt} opacity="0.9"/>
      {[14,22,30,38,46].map((y,i)=>{
        const off = Math.sin(i*Math.PI*0.8)*8
        return <line key={y} x1={20+off} y1={y} x2={40-off} y2={y} stroke="white" strokeWidth="2.5" opacity="0.65"/>
      })}
    </>)

    case "sigma": return g(<>
      {/* Sigma / Σ */}
      <polyline points="46,6 14,6 32,28 14,54 46,54" {...W} strokeWidth="5"/>
    </>)

    case "chart_bar": return g(<>
      {/* Line chart with dots */}
      <polyline points="4,46 18,32 30,38 42,18 56,12" {...W} strokeWidth="4.5"/>
      {[[4,46],[18,32],[30,38],[42,18],[56,12]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="4.5" fill="white" opacity="0.9"/>
      ))}
      <line x1="2" y1="54" x2="58" y2="54" stroke="white" strokeWidth="2.5" opacity="0.5"/>
    </>)

    case "stethoscope": return g(<>
      {/* Ear buds + tube + chestpiece */}
      <path d="M12,8 C12,20 22,26 22,36 C22,46 30,52 38,52 C46,52 52,46 52,38 C52,30 46,24 38,24" {...W} strokeWidth="4.5"/>
      <circle cx="38" cy="24" r="8" {...Wt} opacity="0.8"/>
      <circle cx="38" cy="24" r="3" fill="white" opacity="0.7"/>
      <line x1="8"  y1="6"  x2="16" y2="6"  stroke="white" strokeWidth="3.5"/>
    </>)

    case "cross": return g(<>
      <rect x="22" y="4"  width="16" height="52" rx="4" fill="white" opacity="0.9"/>
      <rect x="4"  y="22" width="52" height="16" rx="4" fill="white" opacity="0.9"/>
    </>)

    case "mortar": return g(<>
      {/* Mortar and pestle */}
      <path d="M10,40 Q10,56 30,56 Q50,56 50,40 Q50,28 30,28 Q10,28 10,40 Z" {...W}/>
      <path d="M30,10 C38,10 48,20 46,30" {...W} strokeWidth="5"/>
      <line x1="20" y1="4" x2="40" y2="4" stroke="white" strokeWidth="3.5" opacity="0.6"/>
    </>)

    case "leaf": return g(<>
      <path d="M30,52 C30,52 6,44 6,20 C6,20 20,8 40,14 C54,18 56,32 50,44 C44,56 30,52 30,52 Z" {...W}/>
      <path d="M30,52 C30,52 30,30 20,16" {...Wt} opacity="0.6"/>
    </>)

    case "fish": return g(<>
      <path d="M6,30 C10,20 20,14 32,14 C44,14 54,22 54,30 C54,38 44,46 32,46 C20,46 10,40 6,30 Z" {...W}/>
      {/* Tail */}
      <path d="M6,30 L-2,20 M6,30 L-2,40" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
      {/* Eye */}
      <circle cx="44" cy="26" r="4" fill="white" opacity="0.9"/>
      {/* Waves */}
      <path d="M22,36 Q26,32 30,36 Q34,40 38,36" {...Wt} opacity="0.5"/>
    </>)

    case "wheat": return g(<>
      {/* Central stalk */}
      <line x1="30" y1="56" x2="30" y2="10" stroke="white" strokeWidth="4"/>
      {/* Grains alternating left/right */}
      {([[[30,46],true],[[30,36],false],[[30,26],true],[[30,16],false]] as [number[], boolean][]).map(([[x,y],right],i)=>(
        <path key={i}
          d={right
            ? `M${x},${y} C${x+16},${y-2} ${x+14},${y-12} ${x},${y-10}`
            : `M${x},${y} C${x-16},${y-2} ${x-14},${y-12} ${x},${y-10}`}
          fill="white" opacity="0.8"
        />
      ))}
    </>)

    case "seedling": return g(<>
      <line x1="30" y1="56" x2="30" y2="28" stroke="white" strokeWidth="4"/>
      <path d="M30,28 C30,28 14,20 12,6 C12,6 26,6 30,28 Z" fill="white" opacity="0.85"/>
      <path d="M30,36 C30,36 44,28 48,14 C48,14 34,14 30,36 Z" fill="white" opacity="0.75"/>
    </>)

    case "coin": return g(<>
      <circle cx="30" cy="30" r="24" {...W}/>
      <circle cx="30" cy="30" r="16" {...Wt} opacity="0.6"/>
      <text x="30" y="36" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold" opacity="0.9">₦</text>
    </>)

    case "bank": return g(<>
      {/* Pillared building */}
      <polygon points="30,4 58,18 2,18" fill="white" opacity="0.9"/>
      <rect x="4"  y="18" width="52" height="4" fill="white" opacity="0.7"/>
      {[10,19,28,37,46].map(x=>(
        <rect key={x} x={x} y="22" width="6" height="26" rx="2" fill="white" opacity="0.75"/>
      ))}
      <rect x="2" y="48" width="56" height="6" rx="2" fill="white" opacity="0.9"/>
    </>)

    case "trend": return g(<>
      {/* Upward arrow + bar chart */}
      <polyline points="4,50 20,34 32,42 46,20 56,8" {...W} strokeWidth="5"/>
      <polyline points="44,8 56,8 56,20" {...W} strokeWidth="4"/>
      <line x1="2" y1="56" x2="58" y2="56" stroke="white" strokeWidth="2.5" opacity="0.5"/>
    </>)

    case "palette": return g(<>
      <path d="M30,4 C16,4 6,16 6,28 C6,42 16,50 26,48 C30,47 30,42 26,40 C22,38 24,34 30,34 C44,34 54,26 54,16 C54,9 43,4 30,4 Z" {...W}/>
      {/* Paint dots */}
      <circle cx="14" cy="24" r="4" fill="white" opacity="0.8"/>
      <circle cx="20" cy="14" r="4" fill="white" opacity="0.8"/>
      <circle cx="32" cy="10" r="4" fill="white" opacity="0.8"/>
      <circle cx="44" cy="14" r="4" fill="white" opacity="0.8"/>
    </>)

    case "pen": return g(<>
      {/* Fountain pen */}
      <path d="M42,4 L52,14 L26,40 L10,46 L16,30 Z" {...W}/>
      <line x1="30" y1="20" x2="44" y2="34" {...Wt} opacity="0.5"/>
      <path d="M10,46 Q14,50 18,46 L16,30" stroke="white" strokeWidth="3.5" fill="none" opacity="0.7"/>
      {/* Underline representing text */}
      <line x1="4" y1="54" x2="34" y2="54" stroke="white" strokeWidth="3" opacity="0.5"/>
    </>)

    case "mask": return g(<>
      {/* Theater comedy/tragedy masks */}
      <path d="M6,8 Q6,30 20,38 Q26,42 26,36 L26,8 Z" {...W}/>
      <path d="M34,8 Q34,30 48,30 Q54,24 54,8 Z" {...W}/>
      <path d="M12,22 Q14,26 18,22" {...Wt} opacity="0.8"/>  {/* smile */}
      <path d="M40,22 Q42,18 46,22" {...Wt} opacity="0.8"/>  {/* frown */}
    </>)

    case "scales": return g(<>
      <line x1="30" y1="4" x2="30" y2="56" stroke="white" strokeWidth="3.5"/>
      <line x1="6" y1="20" x2="54" y2="20" stroke="white" strokeWidth="3.5"/>
      <path d="M6,20 L2,36 Q8,40 16,36 L12,20" {...W} strokeWidth="3"/>
      <path d="M54,20 L50,36 Q56,40 60,36 L56,20" {...W} strokeWidth="3"/>
      <path d="M22,56 L38,56 L34,52 L26,52 Z" fill="white" opacity="0.8"/>
    </>)

    case "mic": return g(<>
      <rect x="20" y="4" width="20" height="30" rx="10" {...W}/>
      <path d="M10,28 Q10,46 30,46 Q50,46 50,28" {...W} strokeWidth="4.5"/>
      <line x1="30" y1="46" x2="30" y2="56" stroke="white" strokeWidth="4.5"/>
      <line x1="18" y1="56" x2="42" y2="56" stroke="white" strokeWidth="4"/>
    </>)

    case "brain": return g(<>
      {/* Simplified brain outline */}
      <path d="M30,52 C18,52 8,44 8,34 C8,28 12,24 16,22 C14,18 14,12 20,10 C24,8 28,10 30,14 C32,10 36,8 40,10 C46,12 46,18 44,22 C48,24 52,28 52,34 C52,44 42,52 30,52 Z" {...W}/>
      <line x1="30" y1="14" x2="30" y2="52" {...Wt} opacity="0.4"/>
      <path d="M14,30 Q22,26 30,30 Q38,34 46,30" {...Wt} opacity="0.5"/>
    </>)

    case "globe": return g(<>
      <circle cx="30" cy="30" r="24" {...W}/>
      <ellipse cx="30" cy="30" rx="12" ry="24" {...Wt} opacity="0.7"/>
      <line x1="6"  y1="30" x2="54" y2="30" stroke="white" strokeWidth="2.5" opacity="0.6"/>
      <line x1="9"  y1="18" x2="51" y2="18" stroke="white" strokeWidth="2" opacity="0.4"/>
      <line x1="9"  y1="42" x2="51" y2="42" stroke="white" strokeWidth="2" opacity="0.4"/>
    </>)

    case "book": return g(<>
      <path d="M6,8 Q6,50 30,52 Q54,50 54,8 L30,12 Z" {...W}/>
      <line x1="30" y1="12" x2="30" y2="52" stroke="white" strokeWidth="3.5"/>
      <line x1="14" y1="22" x2="26" y2="22" {...Wt} opacity="0.5"/>
      <line x1="14" y1="30" x2="26" y2="30" {...Wt} opacity="0.5"/>
      <line x1="14" y1="38" x2="26" y2="38" {...Wt} opacity="0.5"/>
      <line x1="34" y1="22" x2="46" y2="22" {...Wt} opacity="0.5"/>
      <line x1="34" y1="30" x2="46" y2="30" {...Wt} opacity="0.5"/>
      <line x1="34" y1="38" x2="46" y2="38" {...Wt} opacity="0.5"/>
    </>)

    case "camera": return g(<>
      <rect x="4" y="16" width="52" height="38" rx="6" {...W}/>
      <circle cx="30" cy="34" r="12" {...Wt}/>
      <circle cx="30" cy="34" r="5" fill="white" opacity="0.6"/>
      <path d="M18,16 L22,8 L38,8 L42,16" {...Wt} opacity="0.8"/>
    </>)

    case "law_hammer": return g(<>
      <rect x="26" y="6" width="28" height="18" rx="4" {...W}/>
      <line x1="26" y1="18" x2="10" y2="50" stroke="white" strokeWidth="6" strokeLinecap="round"/>
      <line x1="4" y1="52" x2="36" y2="52" stroke="white" strokeWidth="4" strokeLinecap="round" opacity="0.7"/>
    </>)

    default: return g(<>
      <circle cx="30" cy="30" r="22" {...W}/>
    </>)
  }
}

// ─── Accent dots (hash-seeded) ─────────────────────────────────────────────────

function AccentDots({ seed, count = 5 }: { seed: number; count?: number }) {
  const rand = seededRand(seed)
  const dots = Array.from({ length: count }, (_, i) => {
    const x = 16 + rand() * 288   // avoid very edges
    const y = 8  + rand() * 164
    const r = 1.5 + rand() * 2.5
    const op = 0.2 + rand() * 0.35
    return <circle key={i} cx={x} cy={y} r={r} fill="white" opacity={op} />
  })
  return <>{dots}</>
}

// ─── Floating background circles (hash-seeded) ───────────────────────────────

function FloatCircles({ seed, color }: { seed: number; color: string }) {
  const rand = seededRand(seed + 7777)
  const circles = Array.from({ length: 3 }, (_, i) => {
    const cx = 40 + rand() * 240
    const cy = 20 + rand() * 140
    const r  = 30 + rand() * 60
    return (
      <circle key={i} cx={cx} cy={cy} r={r}
        fill={color} opacity={0.13 + rand() * 0.12}
      />
    )
  })
  return <>{circles}</>
}

// ─── Public component ────────────────────────────────────────────────────────

interface CareerIllustrationProps {
  nodeId: string
  broadCategory?: string
  className?: string
}

export function CareerIllustration({ nodeId, broadCategory, className = "" }: CareerIllustrationProps) {
  const cfg   = getConfig(nodeId, broadCategory)
  const hash  = nodeHash(nodeId)
  const gradId = `cg_${nodeId.replace(/\W/g, "_")}`

  return (
    <svg
      viewBox="0 0 320 180"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-full h-full ${className}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor={cfg.g1} />
          <stop offset="100%" stopColor={cfg.g2} />
        </linearGradient>
      </defs>

      {/* Background */}
      <rect width="320" height="180" fill={`url(#${gradId})`} />

      {/* Floating circles */}
      <FloatCircles seed={hash} color="white" />

      {/* Accent dots */}
      <AccentDots seed={hash + 3333} count={6} />

      {/* Centered icon */}
      {renderIcon(cfg.iconType)}
    </svg>
  )
}
